import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import GameLoadingSpinner from '../components/GamePlayer/GameLoadingSpinner';
import GameErrorBoundary from '../components/GamePlayer/GameErrorBoundary';
import api from '../services/api';
import { playVersion } from '../services/versions';
import { isEngineUpgradeRequiredError } from '../services/engineUpgrade';
import { DisplayMode, gameSupportsMobile } from '../models/project';
import { WebBuildState } from '../models/version';
import { useIsMobile } from '../hooks/useIsMobile';
import { isMobileDevice } from '../utils/device';
import { IconArrowL, IconMark } from '../components/Icons';
import TuningEditor, { TuningTabWidth } from '../components/GamePlayer/TuningEditor';
import ControlsStrip, { ControlsStripHeight } from '../components/GamePlayer/ControlsStrip';
import PlayerTabs from '../components/GamePlayer/PlayerTabs';
import CrashReportDialog from '../components/GamePlayer/CrashReportDialog';
import { usePlayerTab } from '../hooks/usePlayerTab';
import { useCrashReport } from '../hooks/useCrashReport';
import ReconnectingIndicator from '../components/ReconnectingIndicator';
import { PipelineEventsProvider } from '../context/PipelineEventsContext';
import { useAuth } from '../context/AuthContext';

const GameFrameName = 'entire-game';
const TuningTabGap = 6;

const getApiServerUrl = () => {
  const envBaseUrl = import.meta.env.VITE_API_BASE_URL;
  if (envBaseUrl) return envBaseUrl;
  if (import.meta.env.DEV) return 'http://localhost:5136';
  return `${window.location.protocol}//${window.location.host}`;
};

interface GamePlayerData {
  projectId: string;
  gameName: string;
  versionId?: string;
  webGlPath: string;
  isReady: boolean;
  buildState?: WebBuildState;
  semver?: string;
  isOwner?: boolean;
  tuningEditorEnabled?: boolean;
  supportedDisplayModes?: DisplayMode[];
}

interface GameInfo {
  id: string;
  name: string;
  description: string;
  creatorName: string;
  thumbnailUrl?: string;
  supportedDisplayModes?: DisplayMode[];
}

const notReadyMessage = (buildState: WebBuildState | undefined, isOwner: boolean): string => {
  if (!isOwner) return 'This game isn’t available right now. Try again later.';
  switch (buildState) {
    case 'Building':
      return 'This version is being built. Open it again in a moment.';
    case 'Failed':
      return 'This version’s build failed. Rebuild it from the version panel, or send a prompt that fixes it.';
    default:
      return 'This version isn’t built yet. Restore it from the version panel first, then try again.';
  }
};

const requestPlay = async (projectId: string, versionId: string | undefined): Promise<GamePlayerData> =>
  (versionId ? await playVersion(projectId, versionId) : (await api.get<GamePlayerData>(`games/${projectId}/play`)).data) as GamePlayerData;

const getResolutionForDisplayMode = (displayMode: DisplayMode | undefined): { width: number; height: number } => {
  switch (displayMode) {
    case 'MobilePortrait':
      return { width: 375, height: 667 };
    case 'MobileLandscape':
      return { width: 667, height: 375 };
    case 'PC':
    default:
      return { width: 1280, height: 720 };
  }
};

export default function GamePlayer() {
  const { projectId, versionId } = useParams<{ projectId: string; versionId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [resolution, setResolution] = useState({ width: 1280, height: 720 });
  const [gameLoaded, setGameLoaded] = useState(false);
  const [gamePlayerData, setGamePlayerData] = useState<GamePlayerData | null>(null);
  const [gameInfo, setGameInfo] = useState<GameInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionStartTime] = useState(Date.now());
  const [frameRun, setFrameRun] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState(600);
  const [availableWidth, setAvailableWidth] = useState(800);
  const isMobile = useIsMobile();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!projectId) return;
    const fetchGameData = async () => {
      try {
        const player = await requestPlay(projectId, versionId);
        if (player.isReady === false) {
          setError(notReadyMessage(player.buildState, Boolean(versionId || player.isOwner)));
          return;
        }
        setGamePlayerData(player);
        const infoResponse = await api.get(`games/${projectId}/info`);
        setGameInfo(infoResponse.data);
        const displayModes = infoResponse.data.supportedDisplayModes || player.supportedDisplayModes;
        if (displayModes && displayModes.length > 0) {
          setResolution(getResolutionForDisplayMode(displayModes[0] as DisplayMode));
        }
      } catch (err) {
        if (isEngineUpgradeRequiredError(err)) {
          setError(
            versionId
              ? 'This version was made with an older engine version and has no build to play. Set it as active and upgrade the engine to continue from it.'
              : 'This game was made with an older engine version and has no build to play. Open the project and upgrade the engine from Builds.',
          );
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load game');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchGameData();
  }, [projectId, versionId]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const sessionDuration = (Date.now() - sessionStartTime) / 1000;
      const baseUrl = window.location.protocol + '//' + window.location.host;
      navigator.sendBeacon(
        `${baseUrl}/api/games/${projectId}/analytics`,
        JSON.stringify({ eventType: 'session_end', sessionDuration }),
      );
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [projectId, sessionStartTime]);

  useEffect(() => {
    if (gameLoaded && iframeRef.current) {
      setTimeout(() => iframeRef.current?.focus(), 50);
    }
  }, [resolution, gameLoaded]);

  const getGameUrl = () => {
    if (!gamePlayerData?.webGlPath) return '';
    if (gamePlayerData.webGlPath.startsWith('http')) return gamePlayerData.webGlPath;
    return `${getApiServerUrl()}/games/${gamePlayerData.webGlPath}`;
  };

  const gameUrl = getGameUrl();
  const [crash, dismissCrash] = useCrashReport(iframeRef, gameUrl);
  const tunedVersionId = versionId ?? gamePlayerData?.versionId;
  const showControls = Boolean(gamePlayerData?.tuningEditorEnabled && gamePlayerData.isOwner && !isMobile && gameUrl);
  const showTuning =
    isAuthenticated &&
    gamePlayerData?.tuningEditorEnabled &&
    gamePlayerData.isOwner &&
    !isMobile &&
    projectId &&
    tunedVersionId &&
    gameUrl;
  const [tab, setTab] = usePlayerTab(projectId ?? '', Boolean(showTuning));
  const editingScenes = Boolean(showTuning) && tab === 'scenes';
  const gamePadding = isMobile ? 12 : 24;
  const gamePaddingRight = showTuning && !editingScenes ? TuningTabWidth + TuningTabGap : gamePadding;

  useEffect(() => {
    const compute = () => {
      if (gameContainerRef.current) {
        const rect = gameContainerRef.current.getBoundingClientRect();
        const newHeight = rect.height - gamePadding * 2;
        const newWidth = rect.width - gamePadding - gamePaddingRight;
        if (newHeight > 0) setAvailableHeight(newHeight);
        if (newWidth > 0) setAvailableWidth(newWidth);
      }
    };
    const t = setTimeout(compute, 50);
    const ro = new ResizeObserver(compute);
    if (gameContainerRef.current) ro.observe(gameContainerRef.current);
    return () => {
      clearTimeout(t);
      ro.disconnect();
    };
    // loading: the container only exists once the player tree renders, so the observer has to be
    // attached again then — otherwise nothing ever re-measures.
  }, [gamePadding, gamePaddingRight, loading]);

  const handleGameLoad = () => {
    setGameLoaded(true);
    dismissCrash();
    setTimeout(() => iframeRef.current?.focus(), 100);
  };

  const restartGame = async () => {
    dismissCrash();
    setGameLoaded(false);
    const fresh = await requestPlay(projectId!, versionId).catch(() => null);
    if (fresh && fresh.isReady !== false && fresh.webGlPath) {
      setGamePlayerData((previous) => previous && { ...previous, ...fresh });
    }
    setFrameRun((run) => run + 1);
  };

  const handleGameError = () => setError('Failed to load the game. Please try refreshing the page.');

  const handleBack = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/');
  };

  const getContainerAndScale = () => {
    const scaleByHeight = (availableHeight - (showControls ? ControlsStripHeight : 0)) / resolution.height;
    const scaleByWidth = availableWidth / resolution.width;
    const scale = Math.min(scaleByHeight, scaleByWidth);
    const containerWidth = Math.round(resolution.width * scale);
    const containerHeight = Math.round(resolution.height * scale);
    return {
      containerSize: { width: containerWidth, height: containerHeight },
      iframeStyle: {
        width: containerWidth,
        height: containerHeight,
      },
    };
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
        <GameLoadingSpinner message="Loading game information…" />
      </div>
    );
  }

  if (error || !gamePlayerData || !gameInfo) {
    return (
      <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: 'var(--bg)', color: 'var(--text)' }}>
        <div style={{ maxWidth: 440, textAlign: 'center', padding: 24 }}>
          <div className="eyebrow" style={{ color: 'var(--danger)', marginBottom: 12 }}>Error</div>
          <h2 className="display tight" style={{ fontSize: 28, margin: '0 0 12px' }}>Game not available</h2>
          <p className="muted-2" style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 24 }}>{error}</p>
          <Link to="/" className="btn primary lg" style={{ textDecoration: 'none' }}>
            <IconArrowL />
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  const supportedDisplayModes = gameInfo.supportedDisplayModes || gamePlayerData.supportedDisplayModes;
  if (isMobileDevice() && !gameSupportsMobile(supportedDisplayModes)) {
    return (
      <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: 'var(--bg)', color: 'var(--text)' }}>
        <div style={{ maxWidth: 440, textAlign: 'center', padding: 24 }}>
          <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: 12 }}>Desktop experience</div>
          <h2 className="display tight" style={{ fontSize: 28, margin: '0 0 12px' }}>This game is built for desktop</h2>
          <p className="muted-2" style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 24 }}>
            It needs a keyboard or mouse — open it on a computer to play.
          </p>
          <Link to="/" className="btn primary lg" style={{ textDecoration: 'none' }}>
            <IconArrowL />
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <GameErrorBoundary projectId={projectId!}>
      <div style={{ height: '100dvh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="topbar" style={isMobile ? { padding: '0 14px' } : undefined}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <button
              type="button"
              onClick={handleBack}
              className="nav-link"
              style={{ padding: '0 6px', background: 'none', border: 'none', cursor: 'pointer' }}
              title="Back"
            >
              <IconArrowL />
            </button>
            {gameInfo.thumbnailUrl ? (
              <img
                src={gameInfo.thumbnailUrl}
                alt={gameInfo.name}
                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8 }}
              />
            ) : (
              <IconMark size={40} />
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{gameInfo.name}</div>
                {gamePlayerData.semver && (
                  <span className="mono muted" style={{ fontSize: 11, flexShrink: 0 }}>v{gamePlayerData.semver}</span>
                )}
              </div>
              <div className="muted" style={{ fontSize: 11 }}>by {gameInfo.creatorName}</div>
            </div>
          </div>
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="chip">
                <span className="dot" style={{ background: 'var(--accent)' }} />
                WebGL · {resolution.width}×{resolution.height}
              </span>
            </div>
          )}
        </div>

        {showTuning && <PlayerTabs tab={tab} onChange={setTab} />}

        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div
          ref={gameContainerRef}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingBlock: gamePadding,
            paddingLeft: gamePadding,
            paddingRight: gamePaddingRight,
            minHeight: 0,
            minWidth: 0,
          }}
        >
          {(() => {
            const { containerSize, iframeStyle } = getContainerAndScale();
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {showControls && <ControlsStrip buildUrl={gameUrl} width={containerSize.width} />}
                <div
                  style={{
                    ...containerSize,
                    background: 'black',
                    borderRadius: 14,
                    overflow: 'hidden',
                    border: '1px solid var(--line)',
                    boxShadow: '0 30px 80px -30px rgba(0,0,0,0.6)',
                    position: 'relative',
                  }}
                  onClick={() => {
                    if (iframeRef.current && gameLoaded) iframeRef.current.focus();
                  }}
                >
                  {!gameLoaded && <GameLoadingSpinner />}
                  <iframe
                    key={frameRun}
                    ref={iframeRef}
                    name={GameFrameName}
                    src={gameUrl}
                    style={{ border: 'none', ...iframeStyle }}
                    onLoad={handleGameLoad}
                    onError={handleGameError}
                    title={gamePlayerData.gameName || gameInfo.name}
                    tabIndex={0}
                  />
                </div>
              </div>
            );
          })()}
        </div>
        {showTuning && (
          <PipelineEventsProvider projectId={projectId}>
            <TuningEditor
              projectId={projectId}
              versionId={tunedVersionId}
              buildUrl={gameUrl}
              view={editingScenes ? 'scenes' : 'panel'}
            />
            <ReconnectingIndicator />
          </PipelineEventsProvider>
        )}
        </div>

        <div
          style={{
            padding: isMobile ? '10px 14px' : '12px 28px',
            borderTop: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            fontSize: 12,
            color: 'var(--text-5)',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{gameInfo.description}</span>
          {!isMobile && <span className="mono" style={{ fontSize: 11, flexShrink: 0 }}>by {gameInfo.creatorName}</span>}
        </div>

        {crash && (
          <CrashReportDialog
            key={crash.id}
            projectId={projectId!}
            report={crash.report}
            version={gamePlayerData.semver ?? crash.report.gameVersionId ?? tunedVersionId ?? null}
            canRequestFix={isAuthenticated && Boolean(gamePlayerData.isOwner)}
            onClose={restartGame}
          />
        )}
      </div>
    </GameErrorBoundary>
  );
}
